import { useEffect, useState, useCallback } from 'react';
import { subscriptionsApi } from '../lib/api';

/**
 * Fetches the public promos exposed by GET /api/subscriptions/plans and
 * returns the first active (non-expired) one plus the raw plans list.
 * The endpoint is public — no auth needed — so this hook is safe to use
 * on the landing page.
 *
 * Also transparently re-fetches when the current window's `expires_at`
 * passes (SUMMER rolls to a new window every Monday 00:00 Paris) so users
 * who leave the tab open across midnight get the fresh countdown without
 * a manual refresh.
 *
 * We intentionally keep the shape flat so callers can destructure:
 *   const { plans, promo, loading } = usePublicPromo();
 * `promo` is null when no promo is currently running.
 */
export function usePublicPromo() {
  const [state, setState] = useState({ plans: [], promo: null, loading: true });

  const load = useCallback(() => {
    let cancelled = false;
    subscriptionsApi.getPlans()
      .then((res) => {
        if (cancelled) return;
        const promos = res.data?.promos || [];
        const now = Date.now();
        const active = promos.find((p) => {
          if (!p.expires_at) return true;
          const exp = Date.parse(p.expires_at);
          return Number.isNaN(exp) || exp > now;
        });
        setState({
          plans: res.data?.plans || [],
          promo: active || null,
          loading: false,
        });
      })
      .catch(() => {
        if (!cancelled) setState({ plans: [], promo: null, loading: false });
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load]);

  // Auto-refresh at expiry: schedule a single timer that re-fetches when
  // the active window ends. This is how the weekly rollover propagates to
  // clients that leave the page open across Sunday→Monday.
  useEffect(() => {
    const exp = state.promo?.expires_at;
    if (!exp) return undefined;
    const ms = Date.parse(exp) - Date.now();
    if (Number.isNaN(ms) || ms <= 0 || ms > 2 ** 31 - 1) return undefined;
    // +1s buffer so the server has ticked into the new window.
    const id = setTimeout(() => load(), ms + 1000);
    return () => clearTimeout(id);
  }, [state.promo?.expires_at, load]);

  return state;
}

/**
 * Returns the promo price for a given plan_type or the fallback base price.
 * Small utility co-located with the hook so all promo maths lives here.
 */
export function promoPriceFor(promo, planType, basePrice) {
  if (!promo) return { finalPrice: basePrice, hasPromo: false };
  const map = promo.per_plan_price || {};
  const applicable = promo.applicable_plans || [];
  if (!applicable.includes(planType)) return { finalPrice: basePrice, hasPromo: false };
  const p = map[planType];
  if (typeof p !== 'number') return { finalPrice: basePrice, hasPromo: false };
  return { finalPrice: p, hasPromo: true };
}
